/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import StatusIndicator from '@ion/dls/src/components/status/StatusIndicator';
import {ScDeviceIcon} from '@ion/dls/src/icons/ScDeviceIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconStatus} from '@ion/dls/src/types/types';

interface PendingDeviceSummary {
  label: string;
  esn: string;
  lastIdReport: string;
  status?: string;
  isClickable?: boolean;
  onClick?: () => void;
}

interface SentinelAssociationPendingDetailsProps {
  primary: PendingDeviceSummary;
  secondary: PendingDeviceSummary;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  detailsContainer : {
    marginTop : '16px'
  },
  detailsGrid : {
    display                        : 'flex',
    gap                            : '16px',
    marginTop                      : '14px',
    [theme.breakpoints.down('sm')] : {
      flexDirection : 'column'
    }
  },
  deviceColumn : {
    display      : 'flex',
    alignItems   : 'flex-start',
    flex         : '1 1 0',
    minWidth     : 0,
    border       : `1px solid ${dlsColor('grey4')}`,
    borderRadius : '6px',
    padding      : '12px'
  },
  deviceIcon : {
    marginRight : '10px',
    marginTop   : '2px',
    flexShrink  : 0
  },
  deviceContent : {
    minWidth : 0
  },
  titleLine : {
    display    : 'flex',
    alignItems : 'center',
    flexWrap   : 'wrap',
    gap        : '6px',
    color      : dlsColor('grey2'),
    fontSize   : '14px !important',
    lineHeight : '24px',
    fontFamily : 'Roboto !important',
    fontWeight : 500
  },
  titleValue : {
    color      : dlsColor('grey2'),
    fontSize   : '14px !important',
    lineHeight : '24px'
  },
  link : {
    textDecoration : 'underline',
    cursor         : 'pointer'
  },
  statusIndicator : {
    marginLeft : '6px'
  },
  lastIdReportLine : {
    color      : dlsColor('grey3'),
    fontSize   : '14px !important',
    lineHeight : '24px',
    marginTop  : '2px',
    fontFamily : 'Roboto !important',
    fontWeight : 400
  }
}));

const path = 'admin-devices:view.sentinel.workInProgress.status';
const DDSAR_STATUS_LABEL_KEYS: Record<string, string> = {
  Disabled       : `${path}.disabled`,
  Enabled        : `${path}.enabled`,
  PendingEnable  : `${path}.pendingEnable`,
  PendingDisable : `${path}.pendingDisable`
};

const getDDSARStatusLabel = (status?: string): string => (
  translate(DDSAR_STATUS_LABEL_KEYS[status ?? ''] || `${path}.unknown`)
);

const DDSAR_ICON_STATUS: Record<string, keyof typeof IconStatus> = {
  Enabled        : 'active',
  PendingEnable  : 'validation',
  PendingDisable : 'offline'
};

const getDDSARIconStatus = (status?: string): IconStatus => (
  IconStatus[DDSAR_ICON_STATUS[status ?? ''] ?? 'parked']
);

const SentinelAssociationPendingDetails = ({
  primary,
  secondary
}: SentinelAssociationPendingDetailsProps) => {
  const classes           = useStyles();
  const lastIdReportLabel = translate('admin-devices:view.sentinel.workInProgress.lastIdReport');

  return (
    <div className={classes.detailsContainer}>
      <div className={classes.detailsGrid}>
        {[primary, secondary].map(({
          label,
          esn,
          lastIdReport,
          status,
          isClickable,
          onClick
        }) => (
          <div key={label} className={classes.deviceColumn}>
            <div className={classes.deviceIcon}>
              <ScDeviceIcon size={24} color={dlsColor('grey3')} />
            </div>
            <div className={classes.deviceContent}>
              <div className={classes.titleLine}>
                <span className={classes.titleValue}>{label}:</span>{' '}
                {isClickable ? (
                  <span className={classnames(classes.titleValue, classes.link)} onClick={onClick}>
                    {esn}
                  </span>
                ) : (
                  <span className={classes.titleValue}>{esn}</span>
                )}
                <span className={classes.statusIndicator}>
                  <StatusIndicator type={getDDSARIconStatus(status)}
                    text={getDDSARStatusLabel(status)} />
                </span>
              </div>
              <div className={classes.lastIdReportLine}>
                {lastIdReportLabel}: {lastIdReport}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default React.memo(SentinelAssociationPendingDetails);
